import { backend_api_v2, paginated_factory_v2 } from '/src/utils/apiv2'
import bus from '/src/utils/event_bus'
import base from "./base"
import { defineStore } from 'pinia'
import { useConstructiblesStore } from "@/stores/constructibles"

let LASTS = {}  // prevent old request fill data over new request.

// Build a uuid -> design.meta lookup from the constructibles store actuals.
// Returns an array of meta objects for the given actual UUIDs, skipping
// any actual or design that isn't loaded yet.
function designMetasForActuals(actualUuids) {
    const cstore = useConstructiblesStore()
    const designsByUuid = cstore.designsByUuid
    const designByActual = new Map()
    for (const m of cstore.modules) designByActual.set(m.uuid, m.design)
    for (const a of cstore.assemblies) designByActual.set(a.uuid, a.design)

    const metas = []
    for (const uuid of actualUuids) {
        const designUuid = designByActual.get(uuid)
        if (!designUuid) continue
        const design = designsByUuid.get(designUuid)
        if (design?.meta) metas.push(design.meta)
    }
    return metas
}

export const useSelectedLogisticsStore = defineStore('selected_logistics', {
    state: () => ({
        selected_actuals: [],
        selected_branch: null,
        selected_size: null,
        selected_part_design: null,
        // The batch the operator is working on, and the batches found in the
        // current selection.
        selected_part_batch: null,
        part_batches: [],
        batch_progress: null,
        branches: [],
        sizes: [],
        parts: [],
        loading: []
    }),
    getters: {
        isLoading: state => tag => state.loading.includes(tag),
        // The batch object behind `selected_part_batch`.
        current_batch: state => state.part_batches.find(
            pb => pb.uuid == state.selected_part_batch) || null,
        // Only a started, unpaused batch unlocks the table.
        batch_running() {
            const batch = this.current_batch
            return !!batch && batch.status == "working" && !batch.paused
        },
        batch_paused() {
            const batch = this.current_batch
            return !!batch && batch.status == "working" && !!batch.paused
        },
    },
    actions: {
        setSelectedActuals(actuals) { this.selected_actuals = actuals },
        setSelectedBranch(branch) { this.selected_branch = branch },
        setSelectedSize(size) { this.selected_size = size },
        setSelectedPartDesign(pd) { this.selected_part_design = pd },
        setSelectedPartBatch(uuid) { this.selected_part_batch = uuid },
        setPartBatches(batches) { this.part_batches = batches },
        setBatchProgress(progress) { this.batch_progress = progress },
        setBranches(branches) { this.branches = branches },
        setSizes(sizes) { this.sizes = sizes },
        setParts(parts) { this.parts = parts },
        replacePart: base.actions.updater("parts"),
        replacePartBatch: base.actions.updater("part_batches"),
        pushLoading(tag) { this.loading.push(tag) },
        popLoading(tag) { this.loading = this.loading.filter(t => t != tag) },

        // LOAD BRANCHES — keys of the design.meta.branch_sizes_map cache
        // built during IFC analysis. No API call needed.
        loadBranchesByActualUuids(payload) {
            const branches = new Set()
            for (const meta of designMetasForActuals(payload)) {
                for (const b of Object.keys(meta.branch_sizes_map || {})) branches.add(b)
            }
            this.setBranches([...branches].sort())
        },
        // LOAD SIZES — union of size UUIDs for the selected branch, looked
        // up by uuid__in against the existing sizes endpoint.
        loadSizesByActualUuidsAndBranch(payload) {
            this.pushLoading("sizes");

            const size_uuids = new Set()
            for (const meta of designMetasForActuals(payload.actuals)) {
                for (const u of meta.branch_sizes_map?.[payload.branch] || []) size_uuids.add(u)
            }

            if (size_uuids.size === 0) {
                this.setSizes([]);
                this.popLoading("sizes");
                return Promise.resolve();
            }

            const path = payload.branch === "timber" ? "profiles" : "plates";
            const url = `logistics/sizes/${path}/?uuid__in=${[...size_uuids].join(",")}&extended_serializer=true`;

            const keyword = `last_logistics_sizes`
            LASTS[keyword] = payload;

            return backend_api_v2.get(url)
                .then(({ data }) => {
                    if (LASTS[keyword] == payload) {
                        this.setSizes(data.results);
                        // Drop a size the new branch/selection does not have:
                        // otherwise the bar marks nothing and the table stays
                        // empty with no explanation.
                        if (this.selected_size
                            && !data.results.some(size => size.uuid == this.selected_size)) {
                            this.setSelectedSize(null);
                        }
                        this.popLoading("sizes");
                    } else {
                        console.log("[i] Rejecting loader commit. Not last call.")
                    }
                })
                .catch(() => bus.emit("notification", { text: `Can't load sizes`, color: "error" }))
        },
        // LOAD PARTS
        loadParts(payload) {
            this.pushLoading("parts");
            let url = `constructibles/parts/?serialize_design=true`;
            // With a batch picked, the batch IS the scope: the tree selection
            // is dropped so a pre-existing batch shows all of its parts, even
            // the ones hanging from constructibles nobody ticked.
            if (payload.part_batch) {
                url += `&part_batch=${payload.part_batch}`;
            } else {
                url += `&actual__in=${payload.actuals}`;
            }
            if (payload.branch) url += `&branch=${payload.branch}`;
            if (payload.size) url += `&size=${payload.size}`;

            const keyword = `last_logistics_parts`
            LASTS[keyword] = payload;

            return paginated_factory_v2(url)
                .then(({ data }) => {
                    if (LASTS[keyword] == payload) {
                        this.setParts(data);
                        this.popLoading("parts");
                    } else {
                        console.log("[i] Rejecting loader commit. Not last call.")
                    }
                })
                .catch(() => bus.emit("notification", { text: `Can't load parts`, color: "error" }))
        },

        // PART BATCHES ------------------------------------------------------

        // The batches the operator can pick: everything still open, plus the
        // ones the parts on the table already belong to. A batch prepared from
        // the parts list has to be selectable here even before its parts load.
        loadSelectablePartBatches(project_uuid) {
            let url = "constructibles/part_batches/?status__in=waiting,working"
                + "&extended_serializer=true&limit=200";
            if (project_uuid && project_uuid != "all") url += `&project=${project_uuid}`;

            const keyword = "last_logistics_part_batches";
            LASTS[keyword] = url;

            return backend_api_v2.get(url)
                .then(({ data }) => {
                    if (LASTS[keyword] != url) return this.part_batches;
                    this.setPartBatches(data.results);
                    // A batch that is gone from the list can't stay selected.
                    if (this.selected_part_batch
                        && !data.results.some(pb => pb.uuid == this.selected_part_batch)) {
                        this.setSelectedPartBatch(null);
                    }
                    return data.results;
                })
                .catch(() => bus.emit("notification", { text: `Can't load part batches`, color: "error" }))
        },

        // Point the branch and size selectors at what the batch actually holds.
        // A batch of a single size — the normal case in the viewer — leaves the
        // operator looking at that size, instead of at an empty selector he has
        // to fill in himself.
        selectBatchScope(uuid) {
            return backend_api_v2
                .get(`constructibles/part_batches/${uuid}/progress/`)
                .then(async ({ data }) => {
                    this.setBatchProgress(data);

                    const sizes = data.sizes || [];

                    // A branch or a size left over from the previous selection
                    // would narrow the batch down to nothing, which reads
                    // exactly like "the batch didn't load".
                    if (this.selected_branch
                        && !sizes.some(size => size.branch == this.selected_branch)) {
                        this.setSelectedBranch(null);
                    }
                    if (this.selected_size
                        && !sizes.some(size => size.size_uuid == this.selected_size)) {
                        this.setSelectedSize(null);
                    }

                    // The branch and size bars are built from the tree
                    // selection. With nothing selected — the normal case when
                    // picking a batch prepared in the parts list — there is no
                    // list to mark anything on, and the batch alone scopes the
                    // table.
                    if (this.selected_actuals.length == 0) return data;

                    if (sizes.length != 1) return data;
                    const only = sizes[0];

                    if (only.branch && this.selected_branch != only.branch) {
                        this.setSelectedBranch(only.branch);
                        // The size list comes from the branch, so it has to be
                        // there before the size can be marked.
                        await this.loadSizesByActualUuidsAndBranch({
                            actuals: this.selected_actuals,
                            branch: only.branch,
                        });
                    }

                    // Only if the current selection actually has it: marking a
                    // size the bar doesn't list would show nothing and narrow
                    // the table to no parts at all.
                    if (this.sizes.some(size => size.uuid == only.size_uuid)) {
                        this.setSelectedSize(only.size_uuid);
                    }
                    return data;
                })
                .catch(() => this.batch_progress)
        },

        // Refresh one batch (after start / pause / finish) without reloading
        // the whole table.
        refreshPartBatch(uuid) {
            return backend_api_v2
                .get(`constructibles/part_batches/${uuid}/?extended_serializer=true`)
                .then(({ data }) => {
                    if (this.part_batches.some(pb => pb.uuid == data.uuid)) {
                        this.replacePartBatch(data);
                    } else {
                        this.setPartBatches([...this.part_batches, data]);
                    }
                    return data;
                })
        },

        startBatch(payload) {
            this.pushLoading("batch");
            return backend_api_v2.post("constructibles/part_batches/start/", payload)
                .then(({ data }) => {
                    this.setPartBatches([
                        ...this.part_batches.filter(pb => pb.uuid != data.part_batch.uuid),
                        data.part_batch,
                    ]);
                    this.setSelectedPartBatch(data.part_batch.uuid);
                    this.setBatchProgress(data.progress);
                    return data;
                })
                .finally(() => this.popLoading("batch"))
        },

        pauseBatch(uuid, paused) {
            this.pushLoading("batch");
            return backend_api_v2
                .post(`constructibles/part_batches/${uuid}/pause/`, { paused })
                .then(({ data }) => {
                    this.replacePartBatch(data.part_batch);
                    this.setBatchProgress(data.progress);
                    return data;
                })
                .finally(() => this.popLoading("batch"))
        },

        finishBatch(uuid, payload) {
            this.pushLoading("batch");
            return backend_api_v2
                .post(`constructibles/part_batches/${uuid}/finish/`, payload)
                .then(({ data }) => {
                    this.replacePartBatch(data.part_batch);
                    this.setBatchProgress(data.progress);
                    // A closed batch leaves the picker — which offers the
                    // waiting and working ones — and stops scoping the table.
                    // Left selected it swallows the view: every size the
                    // operator picks is still filtered down to a batch that is
                    // finished, with no way back.
                    if (!["waiting", "working"].includes(data.part_batch.status)) {
                        this.setPartBatches(this.part_batches.filter(
                            pb => pb.uuid != data.part_batch.uuid));
                        if (this.selected_part_batch == data.part_batch.uuid) {
                            this.setSelectedPartBatch(null);
                        }
                    }
                    return data;
                })
                .finally(() => this.popLoading("batch"))
        },

        reopenBatch(uuid, reason) {
            this.pushLoading("batch");
            return backend_api_v2
                .post(`constructibles/part_batches/${uuid}/reopen/`, { reason })
                .then(({ data }) => {
                    this.replacePartBatch(data.part_batch);
                    this.setBatchProgress(data.progress);
                    return data;
                })
                .finally(() => this.popLoading("batch"))
        },

        // SET READY PARTS

        setReadyPartCount(payload) {
            this.pushLoading("ready_count");
            // with_batch_progress: only the server knows whether the last part
            // of the batch just fell — the table shows the picked sizes only.
            let url = `constructibles/parts/set_ready_count/?serialize_design=true&with_batch_progress=true`;

            return backend_api_v2.post(url, payload)
                .then(({ data }) => {
                    const parts = data.parts || data;
                    parts.forEach(part => this.replacePart(part))
                    // The kit is derived from these counts, so every
                    // constructible the moved parts belong to just changed. The
                    // server returns the fresh ones; without this the per-panel
                    // progress in the left menu never moves and the prompt that
                    // offers to mark a completed kit can never fire, since it
                    // watches for exactly this.
                    useConstructiblesStore().applyKits(data.kits)
                    const progress = (data.part_batches || []).find(
                        pb => pb.uuid == this.selected_part_batch);
                    if (progress) {
                        this.setBatchProgress(progress);
                        // The last part of the batch just fell: the closing
                        // form is the next step, and only the server knows,
                        // since the table shows the picked sizes only.
                        if (progress.completed && progress.running) {
                            bus.emit("events/logistics/batch_completed", progress);
                        }
                    }
                    this.popLoading("ready_count");
                    return data;
                })
        },

    },
})
